import * as React from "react";
import { IconContext, type IconWeight } from "@phosphor-icons/react";

// SAP's icon set. Every icon in the app is imported from here, never from an icon library
// directly, so the whole look can change in this one file.
//
// Icons come from Phosphor (https://phosphoricons.com). They replaced lucide, which SAP's sister
// app Pulse also uses, so SAP doesn't look identical to it. The export names are SAP's own
// vocabulary (many match the old lucide names, so pages only had to change their import line).
// Size icons with classes (`h-4 w-4`); pick a style with `weight` ("regular", "duotone", "fill"…).

export type { Icon as IconComponent, IconProps, IconWeight } from "@phosphor-icons/react";

export {
  Heartbeat as Activity,
  WarningCircle as AlertCircle,
  Warning as AlertTriangle,
  ArrowDown,
  ArrowDownRight,
  ArrowLineDown as ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowsLeftRight as ArrowRightLeft,
  ArrowUp,
  ArrowsDownUp as ArrowUpDown,
  ArrowLineUp as ArrowUpFromLine,
  ArrowUpRight,
  Prohibit as Ban,
  Bell,
  BellRinging as BellRing,
  TextB as Bold,
  BracketsCurly as Braces,
  Buildings as Building2,
  CalendarDots as CalendarClock,
  Camera,
  Check,
  CheckCircle as CheckCircle2,
  CaretDown as ChevronDown,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  CaretUp as ChevronUp,
  Circle,
  WarningCircle as CircleAlert,
  CheckCircle as CircleCheck,
  CurrencyCircleDollar as CircleDollarSign,
  PauseCircle as CirclePause,
  PlayCircle as CirclePlay,
  StopCircle as CircleStop,
  Clock,
  Compass,
  Barricade as Construction,
  Copy,
  ArrowElbowDownLeft as CornerDownLeft,
  Cpu,
  DownloadSimple as Download,
  Eye,
  EyeSlash as EyeOff,
  FileCode as FileJson,
  FilePlus as FilePlus2,
  FileText,
  Flag,
  TreeStructure as FolderTree,
  Gauge,
  Globe,
  HardDrive,
  ClockCounterClockwise as History,
  Hospital,
  Hourglass,
  Image,
  ArrowSquareIn as Import,
  Tray as Inbox,
  CurrencyInr as IndianRupee,
  Info,
  TextItalic as Italic,
  Key as KeyRound,
  SquaresFour as LayoutDashboard,
  GridFour as LayoutGrid,
  Link as Link2,
  ListBullets as List,
  ListNumbers as ListOrdered,
  CircleNotch as LoaderCircle,
  Lock,
  SignOut as LogOut,
  Envelope as Mail,
  EnvelopeSimple as MailX,
  Memory as MemoryStick,
  List as Menu,
  ChatCircleDots as MessageCircleMore,
  Minus,
  Moon,
  DotsThree as MoreHorizontal,
  SidebarSimple as PanelLeftClose,
  SidebarSimple as PanelLeftOpen,
  Pause,
  PencilSimple as Pencil,
  Play,
  Plug,
  PlugCharging as PlugZap,
  Plus,
  Power,
  Receipt as ReceiptText,
  ArrowClockwise as RefreshCw,
  Repeat,
  ArrowClockwise as RotateCw,
  FloppyDisk as Save,
  Scroll as ScrollText,
  MagnifyingGlass as Search,
  MagnifyingGlassMinus as SearchX,
  PaperPlaneRight as Send,
  HardDrives as Server,
  GearSix as Settings,
  SlidersHorizontal as Settings2,
  ShieldCheck,
  ShieldSlash as ShieldOff,
  Sun,
  Tag,
  Trash as Trash2,
  Warning as TriangleAlert,
  TextUnderline as Underline,
  ArrowUUpLeft as Undo2,
  LockOpen as Unlock,
  Plugs as Unplug,
  UserCircle as UserRound,
  UsersThree as Users,
  Wallet,
  WhatsappLogo as WhatsApp,
  FlowArrow as Workflow,
  X,
} from "@phosphor-icons/react";

/** Default weight for every icon; the sidebar and empty states opt into "duotone". */
export const DEFAULT_ICON_WEIGHT: IconWeight = "regular";

const ICON_DEFAULTS = { weight: DEFAULT_ICON_WEIGHT, mirrored: false };

/** Sets app-wide icon defaults; mounted once in App.tsx. */
export const IconProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <IconContext.Provider value={ICON_DEFAULTS}>{children}</IconContext.Provider>
);
